import React, { useCallback, useEffect, useRef, useState } from "react";
import MainModal from "components/MainModal";
import { Box, Button, Drawer, IconButton, Typography } from "@mui/material";
import SrcAddress from "./components/SrcAddress";
import DstAddress from "./components/DstAddress";
import DetailsSection from "./components/DetailsSection";
import TimeSection from "./components/TimeSection";
import OptionsSection from "./components/OptionsSection";
import TaxiClassSection from "./components/TaxiClassSection";
import SignIn from "pages/SignIn";
import CreatedOrder from "./components/CreatedOrder";
import Profile from "pages/Profile";
import ConfirmModal from "components/ConfirmModal";
import SearchModal from "./components/SearchModal";
import { initialOrderData } from "./constants";
import { transformBoxesToRoutes } from "./components/DstAddress/constants";
import { removeAddCost, setCostDetails } from "store/slices/orderCost";
import {
  useCreateOrderMutation,
  useGetClientProfileQuery,
  useGetNearestGeoDataQuery,
  useGetTariffsMutation,
  useUpdateAddCostMutation,
} from "store/api";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import { useAppSelector } from "store/hooks/useAppSelector";
import useNotificationToast from "hooks/useNotificationToast/useNotificationToast";
import useIsMobile from "hooks/useIsMobile";
import { setOrderId } from "store/slices/orderDetails";
import { findErrorById } from "config/constants";
import { signOut } from "store/slices/auth";
import MenuIcon from "@mui/icons-material/Menu";
import { paymentTypes } from "./components/PaymentSection/constants";
import { shallowEqual } from "react-redux";
import { IRoute } from "store/api/types";
import { setTariffs } from "store/slices/tariffs";
import { orderStatuses } from "./components/CreatedOrder/constants";
import pageRootStyles from "pages/PageRoot/PageRoot.styled";
import styles from "./OrderModal.styled";

const OrderModal = () => {
  const [isMainInfo, setIsMainInfo] = useState<boolean>(true);
  const [isTimeInfo, setIsTimeInfo] = useState<boolean>(false);
  const [isOptionsInfo, setIsOptionsInfo] = useState<boolean>(false);
  const [isTaxiClassInfo, setIsTaxiClassInfo] = useState<boolean>(false);
  const [isSignInOpen, setIsSignInOpen] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [startPoint, setStartPoint] = useState<IRoute | null>(null);
  const [getTariffs] = useGetTariffsMutation();
  const dispatch = useAppDispatch();
  const orderDetails = useAppSelector(
    (state) => state.orderDetails,
    shallowEqual,
  );
  const address = useAppSelector((state) => state.srcAddress, shallowEqual);
  const boxes = useAppSelector((state) => state.dstAddress.boxes, shallowEqual);
  const noRoutes = useAppSelector(
    (state) => state.dstAddress.noRoutes,
    shallowEqual,
  );
  const timeData = useAppSelector((state) => state.arriveTime, shallowEqual);
  const checkedOptions = useAppSelector(
    (state) => state.options.checkedOptions,
    shallowEqual,
  );
  const customCheckedOptions = useAppSelector(
    (state) => state.options.customCheckedOptions,
    shallowEqual,
  );
  const taxiClass = useAppSelector((state) => state.taxiClass, shallowEqual);
  const tariffs = useAppSelector((state) => state.tariffs, shallowEqual);
  const orderCost = useAppSelector((state) => state.orderCost, shallowEqual);
  const auth = useAppSelector((state) => state.auth.token, shallowEqual);
  const pushToken = useAppSelector(
    (state) => state.pushToken.token,
    shallowEqual,
  );
  const settings = useAppSelector(
    (state) => state.settings.config,
    shallowEqual,
  );
  const payment = useAppSelector((state) => state.payment, shallowEqual);
  const { data: clientData } = useGetClientProfileQuery(undefined, {
    skip: !auth,
  });
  const { data: startPointData, isSuccess } = useGetNearestGeoDataQuery({
    lat: settings.cityCenter.lat,
    lng: settings.cityCenter.lng,
  });
  const [createOrder] = useCreateOrderMutation();
  const [updateAddCost] = useUpdateAddCostMutation();
  const isMobile = useIsMobile();

  const shouldFetchTariffs = useRef(true);
  const prevBoxesRef = useRef(boxes);
  const prevHouseRef = useRef(address.srcAddressRoute.lat);
  const prevNoRoutesRef = useRef(noRoutes);

  useEffect(() => {
    if (isSuccess && startPointData) {
      if (startPointData.geo_streets.geo_street.length) {
        const startAddress = startPointData.geo_streets.geo_street[0];
        setStartPoint({
          name: startAddress.name,
          number: startAddress.houses[0].house,
          lat: startAddress.houses[0].lat,
          lng: startAddress.houses[0].lng,
        });
      }
      if (startPointData.geo_objects.geo_object.length) {
        const startObject = startPointData.geo_objects.geo_object[0];
        setStartPoint({
          name: startObject.name,
          lat: startObject.lat,
          lng: startObject.lng,
        });
      }
    }
  }, [startPointData, isSuccess]);

  useEffect(() => {
    if (startPoint && !orderDetails.id) {
      const fetchData = async () => {
        try {
          {
            const requestData = {
              ...initialOrderData,
              taxiColumnId: settings.taxiColumnId,
              ["route_undefined"]: true,
              route: [startPoint],
              ...(settings.tariffs
                ? {
                    ["calculated_tariff_names"]: [
                      ...settings.tariffs.map((tariff) => tariff.name),
                    ],
                  }
                : { ["calculated_tariff_names"]: [] }),
              ["flexible_tariff_name"]: taxiClass.value,
            };
            const priceData = await getTariffs(requestData).unwrap();
            dispatch(setCostDetails(priceData[0].order_cost_details));
            dispatch(setTariffs(priceData));
          }
        } catch (error) {
          useNotificationToast({
            type: "error",
          });
        }
      };

      fetchData();
    }
  }, [startPoint, tariffs.length, orderDetails.id]);

  useEffect(() => {
    if (orderDetails.id) {
      setIsMainInfo(false);
    } else {
      setIsMainInfo(true);
    }
  }, [orderDetails.id]);

  useEffect(() => {
    const oneOfBoxesEmpty = boxes.some((box) => !box.lat);

    const fetchData = async () => {
      try {
        if (
          (!oneOfBoxesEmpty && address.srcAddressRoute.lat) ||
          (noRoutes && address.srcAddressRoute.lat)
        ) {
          const requestData = {
            ...initialOrderData,
            taxiColumnId: settings.taxiColumnId,
            ["route_undefined"]: noRoutes,
            ...(noRoutes
              ? { route: [address.srcAddressRoute] }
              : {
                  route: [
                    address.srcAddressRoute,
                    ...transformBoxesToRoutes(boxes),
                  ],
                }),
            reservation: timeData.reservation,
            ...(timeData.reservation
              ? { ["required_time"]: timeData.dateTime }
              : { ["required_time"]: null }),
            ...(settings.tariffs
              ? {
                  ["calculated_tariff_names"]: [
                    ...settings.tariffs.map((tariff) => tariff.name),
                  ],
                }
              : { ["calculated_tariff_names"]: [] }),
            ["flexible_tariff_name"]: taxiClass.value,
            ["extra_charge_codes"]: checkedOptions,
            ...(customCheckedOptions.length
              ? {
                  ["custom_extra_charges"]: [
                    ...customCheckedOptions.map((option) => option.Id),
                  ],
                }
              : { ["custom_extra_charges"]: [] }),
          };
          const priceData = await getTariffs(requestData).unwrap();
          dispatch(setCostDetails(priceData[0].order_cost_details));
          dispatch(setTariffs(priceData));
        }
      } catch (error) {
        useNotificationToast({
          type: "error",
        });
      }
    };

    const prevBoxes = prevBoxesRef.current;
    const prevHouse = prevHouseRef.current;
    const prevNoRoutes = prevNoRoutesRef.current;

    const boxesChanged =
      prevBoxes.length !== boxes.length ||
      boxes.some(
        (box, index) =>
          box.lat !== prevBoxes[index]?.lat ||
          JSON.stringify(box) !== JSON.stringify(prevBoxes[index]),
      );

    const houseChanged = prevHouse !== address.srcAddressRoute.lat;
    const noRoutesBecameFalse = prevNoRoutes && !noRoutes;

    if (
      boxesChanged ||
      shouldFetchTariffs.current ||
      noRoutesBecameFalse ||
      houseChanged ||
      noRoutes
    ) {
      fetchData();
      shouldFetchTariffs.current = false;
    }

    prevBoxesRef.current = boxes;
    prevHouseRef.current = address.srcAddressRoute.lat;
    prevNoRoutesRef.current = noRoutes;
  }, [
    noRoutes,
    boxes,
    address.srcAddressRoute.lat,
    checkedOptions.length,
    customCheckedOptions.length,
  ]);

  useEffect(() => {
    const body = document.body;

    const updateOverflow = () => {
      if (isMobile) {
        if (isSignInOpen || isDrawerOpen || isProfileOpen) {
          body.style.setProperty("overflow", "hidden", "important");
        } else {
          body.style.setProperty("overflow", "auto", "important");
        }
      } else {
        body.style.setProperty("overflow", "hidden", "important");
      }
    };

    updateOverflow();

    const observer = new MutationObserver(() => {
      updateOverflow();
    });

    observer.observe(body, { attributes: true, attributeFilter: ["style"] });

    return () => {
      observer.disconnect();
      body.style.removeProperty("overflow");
    };
  }, [isMobile, isSignInOpen, isDrawerOpen, isProfileOpen]);

  const handleTimeClick = useCallback(() => {
    setIsMainInfo((prev) => !prev);
    setIsTimeInfo((prev) => !prev);
  }, []);

  const handleTimeBackClick = useCallback(() => {
    setIsMainInfo(true);
    setIsTimeInfo(false);
    shouldFetchTariffs.current = true;
  }, []);

  const handleOptionsClick = useCallback(() => {
    setIsMainInfo((prev) => !prev);
    setIsOptionsInfo((prev) => !prev);
  }, []);

  const handleOptionsBackClick = useCallback(() => {
    setIsMainInfo((prev) => !prev);
    setIsOptionsInfo((prev) => !prev);
    shouldFetchTariffs.current = true;
  }, []);

  const handleTaxiClassClick = useCallback(() => {
    setIsMainInfo((prev) => !prev);
    setIsTaxiClassInfo((prev) => !prev);
  }, []);

  const handleTaxiClassBackClick = useCallback(() => {
    setIsMainInfo((prev) => !prev);
    setIsTaxiClassInfo((prev) => !prev);
    shouldFetchTariffs.current = true;
  }, []);

  const handleClose = useCallback(() => {
    setIsSignInOpen((prev) => !prev);
  }, []);

  const handleSingIn = () => {
    setIsSignInOpen((prev) => !prev);
    if (isMobile) {
      setIsDrawerOpen((prev) => !prev);
    }
  };

  const handleCreateOrder = () => {
    const fetchData = async () => {
      try {
        const requestData = {
          ...initialOrderData,
          taxiColumnId: settings.taxiColumnId,
          ["add_cost"]: orderCost.costDetails.add_cost,
          ["route_undefined"]: noRoutes,
          ...(noRoutes
            ? { route: [address.srcAddressRoute] }
            : {
                route: [
                  address.srcAddressRoute,
                  ...transformBoxesToRoutes(boxes),
                ],
              }),
          reservation: timeData.reservation,
          ...(timeData.reservation
            ? { ["required_time"]: timeData.dateTime }
            : { ["required_time"]: null }),
          ["route_address_entrance_from"]: address.entrance,
          comment: address.comment,
          ["calculated_tariff_names"]: [taxiClass.value],
          ["flexible_tariff_name"]: taxiClass.value,
          ["user_full_name"]:
            clientData?.user_first_name ||
            clientData?.user_middle_name ||
            clientData?.user_last_name
              ? `${clientData?.user_first_name || ""} ${
                  clientData?.user_middle_name || ""
                } ${clientData?.user_last_name || ""}`.trim()
              : "&nbsp;",

          ["user_phone"]: clientData?.user_phone || "",
          ["app_registration_token"]: pushToken,
          ["extra_charge_codes"]: checkedOptions,
          ...(customCheckedOptions.length
            ? {
                ["custom_extra_charges"]: [
                  ...customCheckedOptions.map((option) => option.Id),
                ],
              }
            : { ["custom_extra_charges"]: [] }),
          ...(payment.title === paymentTypes[1].title
            ? {
                ["pay_bonuses"]: Number(orderCost.costDetails.order_cost),
              }
            : {}),
          ["payment_type"]: payment.value,
          ...(payment.value === paymentTypes[2].value
            ? {
                ["card_uid"]: payment.card.cardGuid,
              }
            : {}),
        };
        const orderData = await createOrder(requestData).unwrap();
        dispatch(setOrderId(orderData.dispatching_order_uid));
        const fetch = async () => {
          try {
            await updateAddCost({
              id: orderData.dispatching_order_uid,
              amount: orderCost.costDetails.add_cost,
            }).unwrap();
            dispatch(removeAddCost());
          } catch (error) {
            useNotificationToast({
              type: "error",
              message: error.data.Message,
            });
          }
        };
        fetch();
      } catch (error) {
        useNotificationToast({
          type: "error",
          message: findErrorById(error.data.Id),
        });
      }
    };

    if (
      (!address.srcAddressRoute.name && !address.srcAddressRoute.number) ||
      (!noRoutes && boxes.some((box) => !box.address))
    ) {
      useNotificationToast({
        type: "error",
        message: "Будь-ласка, заповніть необхідні поля",
      });
    } else {
      fetchData();
    }
  };

  const handleToggleDrawer = () => setIsDrawerOpen((prev) => !prev);

  const handleToggleProfile = () => {
    setIsProfileOpen((prev) => !prev);
    setIsDrawerOpen(false);
  };

  const handleLogOut = () => {
    dispatch(signOut());
    setIsConfirmModalOpen((prev) => !prev);
  };

  const handleToggleConfirmModal = () => setIsConfirmModalOpen((prev) => !prev);

  return (
    <>
      <MainModal
        isOpen
        sxWrap={styles.modalWrap(
          !!orderDetails.id &&
            (orderDetails.status === orderStatuses[0].value ||
              orderDetails.status === orderStatuses[1].value),
        )}
        sx={styles.modal}
      >
        <Box sx={styles.contentWrap(!!orderDetails.id)}>
          {isMainInfo && (
            <>
              {isMobile && (
                <>
                  <Drawer
                    anchor="right"
                    open={isDrawerOpen}
                    onClose={handleToggleDrawer}
                    sx={styles.drawer}
                  >
                    <Typography
                      sx={styles.drawerContent}
                      onClick={auth ? handleToggleProfile : handleSingIn}
                    >
                      {auth ? "Профіль" : "Вхід"}
                    </Typography>
                    {auth && (
                      <Typography
                        sx={styles.drawerContent}
                        onClick={handleToggleConfirmModal}
                      >
                        Вихід
                      </Typography>
                    )}
                  </Drawer>
                  <IconButton
                    sx={styles.burgerIcon}
                    onClick={handleToggleDrawer}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Profile
                    isOpen={isProfileOpen}
                    onClose={handleToggleProfile}
                  />
                  <ConfirmModal
                    openModal={isConfirmModalOpen}
                    modalClose={handleToggleConfirmModal}
                    handleAction={handleLogOut}
                    titleMessage="Ви дійсно хочете вийти?"
                    buttonMessage="Так"
                    secondBtnMessage="Ні"
                  />
                </>
              )}
              <Typography variant="h2" sx={styles.header}>
                Замовлення таксі
              </Typography>
              <Box sx={styles.innerContent}>
                <SrcAddress />
                <DstAddress />
                <DetailsSection
                  onTimeClick={handleTimeClick}
                  onOptionsClick={handleOptionsClick}
                  onTaxiClassClick={handleTaxiClassClick}
                />
              </Box>
              <Box sx={styles.btnWrap}>
                <Button
                  sx={styles.btn(settings.accentColor)}
                  type="button"
                  onClick={auth ? handleCreateOrder : handleSingIn}
                >
                  Замовити
                </Button>
              </Box>
            </>
          )}
          {isTimeInfo && <TimeSection onBackClick={handleTimeBackClick} />}
          {isOptionsInfo && (
            <OptionsSection onBackClick={handleOptionsBackClick} />
          )}
          {isTaxiClassInfo && (
            <TaxiClassSection onBackClick={handleTaxiClassBackClick} />
          )}
          {orderDetails.id && <CreatedOrder />}
        </Box>
      </MainModal>
      <MainModal
        isOpen={isSignInOpen}
        handleClose={handleClose}
        sx={{ ...styles.modal, ...pageRootStyles.modal }}
      >
        <SignIn onSave={handleClose} />
      </MainModal>
      <MainModal
        isOpen={
          !!orderDetails.id &&
          (orderDetails.status === orderStatuses[0].value ||
            orderDetails.status === orderStatuses[1].value)
        }
        sx={{ ...styles.modal, ...styles.searchModal }}
        sxWrap={styles.searchModalWrap}
      >
        <SearchModal />
      </MainModal>
    </>
  );
};

export default OrderModal;
